import { createSelector } from 'reselect';
import { List as ImmutableList, OrderedMap } from 'immutable';

import { getAccountsById, getGoalAccountIds } from 'data/accounts/selectors';
import { getCategoriesById, isIncomeCat, isExpenseCat } from 'data/categories/selectors';
import { getLogger } from 'utils/logger';
import { isSplitTxn } from 'data/transactions/utils';
import createCachedSelector, { LruCacheObject } from 're-reselect';

import { mkChartOfAccount, ChartOfAccountNode, CoaTypeEnum, BalanceAdjustmentTypeEnum } from './types';
import { coasAreEqual, getCoaId, getBalanceAdjustmentString, unSyncedIdsList, TRANSFER_NOT_FOUND_COA_ID } from './utils';

const log = getLogger('data/chartOfAccounts/selectors.js');

const createCoaNode = (categoriesByParentId, path, pathName, data, children) => {
  const childNodes = children ?
    new ImmutableList(
      children
        .filter((child) => child.id && child.id.length > 0 && child.id !== '0') // if add account fails then coa.id === '0' because of optimistic update bug
        .map((child) => {
          const innerChildren = categoriesByParentId.get(child.id);
          return createCoaNode(categoriesByParentId, [...path, data.id], `${pathName}${data.name}:`, child, innerChildren);
        }),
    ) : null;
  return ChartOfAccountNode({
    id: data.id,
    coa: data.coa ? mkChartOfAccount({ id: data.coa.id, type: data.coa.type }) : null,
    name: data.name,
    inList: data.inList,
    fullPathName: `${pathName}${data.name}`,
    path: new ImmutableList(path),
    children: childNodes,
  });
};

export const getChartOfAccountsTree = createSelector(
  (state, props) => props && props.filterFn,
  getCategoriesById,
  getAccountsById,
  getGoalAccountIds,
  (filterFn, categoriesById, accountsById, goalAccountIds) => {

    if (!categoriesById || categoriesById.size === 0) {
      return new ImmutableList();
    }

    const coasByParentId = categoriesById.reduce(
      (map, category) => {
        const parentId = category.parentId || '0';
        map.set(parentId, map.get(parentId) || []);

        const coa = { type: 'CATEGORY', id: category.id };
        map.get(parentId).push({
          id: getCoaId(coa),
          coa,
          name: category.name,
        });

        return map;
      },
      new Map(),
    );

    // going to add other root items, so make sure root exists - it should
    //
    coasByParentId.set('0', coasByParentId.get('0') || []);

    // add NONE, used to represent "No Category"
    //
    const noneCoa = { type: 'NONE', id: '0' };
    coasByParentId.get('0').push({
      id: 'NONE',
      coa: noneCoa,
      name: '- None -',
    });

    // add balance adjustment chart of accounts
    //
    const baCoa = { type: 'BALANCE_ADJUSTMENT', id: '0' };
    coasByParentId.get('0').push({
      id: 'BALANCE_ADJUSTMENT_0',
      coa: baCoa,
      name: getBalanceAdjustmentString(baCoa.id), // 'Balance Adjustment',
    });

    // add balance adjustment (credit-card payment) chart of accounts
    //
    const ccPaymentCoa = { type: 'BALANCE_ADJUSTMENT', id: '3' };
    coasByParentId.get('0').push({
      id: 'BALANCE_ADJUSTMENT_3',
      coa: ccPaymentCoa,
      name: getBalanceAdjustmentString(ccPaymentCoa.id),
      // name: 'Credit Card Payment',
    });

    // add transfers (accounts and balance adjustment (external transfer)) chart of accounts
    //
    const transferCoa = { type: 'BALANCE_ADJUSTMENT', id: '2' };   // a transfer defaults to an external transfer
    coasByParentId.get('0').push({
      id: 'BALANCE_ADJUSTMENT_2',
      coa: transferCoa,
      name: getBalanceAdjustmentString(transferCoa.id),
      // name: 'Transfer',
    });
    const transfers = [];
    accountsById.valueSeq().forEach((account) => {
      const coa = { type: 'ACCOUNT', id: account.id };
      transfers.push({
        id: getCoaId(coa),
        coa,
        name: `${account.name}`,
        inList: !goalAccountIds.contains(account.id),
      });
    });
    coasByParentId.set('BALANCE_ADJUSTMENT_2', transfers);

    unSyncedIdsList.forEach((uid) => {
      coasByParentId.get('0').push({
        id: uid,
        coa: { type: 'ACCOUNT', id: uid },
        name: 'ACCOUNT NOT SYNCED',
        inList: false,
      });
    });
    //
    // special id for account not found
    coasByParentId.get('0').push({
      id: TRANSFER_NOT_FOUND_COA_ID,
      coa: { type: 'ACCOUNT', id: TRANSFER_NOT_FOUND_COA_ID },
      name: 'Transfer',
      inList: false,
    });

    const uncategorizedCoa = { type: 'UNCATEGORIZED', id: '0' };
    coasByParentId.get('0').push({
      id: 'UNCATEGORIZED',
      coa: uncategorizedCoa,
      name: 'Uncategorized',
    });

    // sort the categories for each parent
    coasByParentId.forEach((categories) => {
      categories.sort((a, b) => (
        a.name.localeCompare(
          b.name,
          undefined,
          { numeric: true, sensitivity: 'base' }
        )
      ));
    });

    const coaTree = new ImmutableList(
      coasByParentId.get('0').map((data) => {
        const children = coasByParentId.get(data.id);
        return createCoaNode(coasByParentId, [], '', data, children);
      })
    );

    return coaTree;
  }
);

const getChartOfAccountsByIdForNode = (node) => {
  let flattenedList = new ImmutableList([ new ImmutableList([node.id, node]) ]);
  if (node.children) {
    flattenedList = node.children.reduce((list, child) => (
      list.concat(getChartOfAccountsByIdForNode(child))
    ), flattenedList);
  }
  return flattenedList;
};


export const getChartOfAccountNodesById = createSelector(
  getChartOfAccountsTree,
  (coaTree) => {
    log.log('Calling getChartOfAccountNodesById selector...');
    if (!coaTree || coaTree.size === 0) {
      return new OrderedMap();
    }
    return new OrderedMap(coaTree.reduce((list, node) => (
      list.concat(getChartOfAccountsByIdForNode(node))
    ), new ImmutableList()));
  }
);

export const getChartOfAccountNodesByCoa = createSelector(
  (state, props) => props.coa,
  getChartOfAccountNodesById,
  (coa, coaNodeList) => {
    log.log('Calling getChartOfAccountNodesByCoa selector...', coaNodeList.toJS(), coa);
    if (!coaNodeList || coaNodeList.size === 0) {
      return new OrderedMap();
    }
    const parentNode = coaNodeList.find((x) => coasAreEqual(x.coa, coa));
    if (parentNode) {
      const allNodes = getChartOfAccountsByIdForNode(parentNode);
      return allNodes.map((x) => x.get(1));
    }
    return new ImmutableList();
  }
);


export const getTransferCOATree = createSelector(
  getAccountsById,
  (accountsById) => {

    const coasByParentId = new Map();

    coasByParentId.set('0', coasByParentId.get('0') || []);

    // add transfers (accounts and balance adjustment (external transfer)) chart of accounts
    //
    const transferCoa = { type: 'BALANCE_ADJUSTMENT', id: '2' };   // a transfer defaults to an external transfer
    coasByParentId.get('0').push({
      id: 'BALANCE_ADJUSTMENT_2',
      coa: transferCoa,
      name: getBalanceAdjustmentString(transferCoa.id),
      // name: 'Transfer',
    });
    accountsById.valueSeq().forEach((account) => {
      const coa = { type: 'ACCOUNT', id: account.id };
      coasByParentId.get('0').push({
        id: getCoaId(coa),
        coa,
        name: `${account.name}`,
      });
    });

    unSyncedIdsList.forEach((uid) => {
      coasByParentId.get('0').push({
        id: uid,
        coa: { type: 'ACCOUNT', id: uid },
        name: 'ACCOUNT NOT SYNCED',
        inList: false,
      });
    });
    //
    // special id for account not found
    coasByParentId.get('0').push({
      id: TRANSFER_NOT_FOUND_COA_ID,
      coa: { type: 'ACCOUNT', id: TRANSFER_NOT_FOUND_COA_ID },
      name: 'ACCOUNT NOT FOUND',
      inList: false,
    });

    // const uncategorizedCoa = { type: 'UNCATEGORIZED', id: '0' };
    // coasByParentId.get('0').push({
    //   id: 'UNCATEGORIZED',
    //   coa: uncategorizedCoa,
    //   name: 'Uncategorized',
    // });

    // sort the categories for each parent
    coasByParentId.forEach((categories) => {
      categories.sort((a, b) => (
        a.name.localeCompare(
          b.name,
          undefined,
          { numeric: true, sensitivity: 'base' }
        )
      ));
    });

    const coaTree = new ImmutableList(
      coasByParentId.get('0').map((data) => {
        const children = coasByParentId.get(data.id);
        return createCoaNode(coasByParentId, [], '', data, children);
      })
    );

    return coaTree;
  }
);

export const COABucketEnum = Object.freeze({
  EXPENSE: 'EXPENSE',
  INCOME: 'INCOME',
  TRANSFER: 'TRANSFER',
});

export const getCOABucketForTransaction = createCachedSelector(
  (state, transaction) => isSplitTxn(transaction),
  (state, transaction) => transaction.coa && transaction.coa.type === CoaTypeEnum.CATEGORY ? isIncomeCat(state, transaction.coa.id) : undefined,
  (state, transaction) => transaction.coa,
  (state, transaction) => transaction.amount,
  (isSplit, isIncome, coa, amount) => {
    let coaType;
    if (isSplit) {
      coaType = amount > 0 ? COABucketEnum.INCOME : COABucketEnum.EXPENSE;
    } else switch (coa && coa.type){
      case "CATEGORY":
        coaType = isIncome ? COABucketEnum.INCOME : COABucketEnum.EXPENSE;
        break;
      case "ACCOUNT":
      case "BALANCE_ADJUSTMENT":
        coaType=COABucketEnum.TRANSFER;
        break;
      case "UNCATEGORIZED":
        coaType=COABucketEnum.EXPENSE;
        break;
      default:
        coaType=COABucketEnum.EXPENSE;
        break;
    }
    return coaType;
  }
)(
  (state, transaction) => `${isSplitTxn(transaction)}:${transaction.coa && transaction.coa.type}:${transaction.coa && transaction.coa.id}:${transaction.amount}`,
  { cacheObject: new LruCacheObject({ cacheSize: 10 }) },
);

export const getCOABucketForSplitTransaction = createCachedSelector(
  (state, split) => split.coa.type === CoaTypeEnum.CATEGORY ? isIncomeCat(state, split.coa.id) : undefined,
  (_state, split) => split,
  (isIncome, split) => {
    let coaType;
    switch (split.coa && split.coa.type){
      case "CATEGORY":
        coaType = isIncome ? COABucketEnum.INCOME : COABucketEnum.EXPENSE;
        break;
      case "ACCOUNT":
      case "BALANCE_ADJUSTMENT":
        coaType = COABucketEnum.TRANSFER;
        break;
      case "UNCATEGORIZED":
        coaType = split.amount > 0 ? COABucketEnum.INCOME : COABucketEnum.EXPENSE;
        break;
      default:
        coaType=COABucketEnum.EXPENSE;
        break;
    }
    return coaType;
  }
)(
  (_state, split) => `${split.coa.id}:${split.coa.type}`,
  { cacheObject: new LruCacheObject({ cacheSize: 10 }) },
);

export const isBill = createCachedSelector(
  (state, accountId, coa) => coa && coa.type === CoaTypeEnum.BALANCE_ADJUSTMENT && coa.id === BalanceAdjustmentTypeEnum.CREDIT_CARD_PAYMENT,
  (state, accountId, coa) => coa && coa.type === CoaTypeEnum.CATEGORY ? isExpenseCat(state, coa.id) : undefined,
  (state, accountId) => getAccountsById(state).get(accountId),
  (state, _accountId, coa) => coa && coa.type === CoaTypeEnum.ACCOUNT ? getAccountsById(state).get(coa.id) : undefined,
  (isCreditCardPayment, isExpense, accountFrom, accountTo) => isCreditCardPayment || isExpense || (accountFrom && accountTo && (['CREDIT', 'LOAN'].includes(accountFrom.type) || ['CREDIT', 'LOAN'].includes(accountTo.type))),
)(
  (_state, coa) => `${coa && coa.type}:${coa && coa.id}`,
  {cacheObject: new LruCacheObject({cacheSize: 10})},
);
